import { type RefObject, useCallback, useRef } from 'react';

/** Высоты строк шапки таблицы на экране и узел, у которого они измеряются */
type Result = {
  /** Узел, в котором список рисует таблицу */
  gridRef: RefObject<HTMLDivElement | null>;
  /** Высоты строк шапки в пикселях сверху вниз; таблица ещё не нарисована — значения нет */
  getHeaderRowHeights: () => number[] | undefined;
};

/**
 * Высоты строк шапки таблицы — их повторяет файл выгрузки. Своих значений у них нет: шапку тянут
 * переносы в наименованиях колонок и направление заголовков, а решает её браузер, — поэтому она
 * измеряется. Измеряется в момент скачивания: наблюдать за ней постоянно значило бы ререндерить
 * список на каждое перетаскивание края колонки ради значения, которое нужно один раз.
 *
 * Измеряется построчно, а не всей шапкой разом: настроенная пользователем высота относится к строке
 * наименований колонок, а строка группы над ними стоит своей высотой — общая высота, разделённая
 * между ними в файле, обе строки поставила бы не туда.
 *
 * Строки берутся по границам ячеек, а не по рядам сетки, которой шапку рисует кит: рядов там
 * столько, какова глубина дерева колонок, а не сколько строк видно, — группа без видимых колонок
 * оставляет свой ряд схлопнутым, и высоты по рядам разошлись бы со строками файла.
 *
 * Опора измерения — роль ячейки заголовка: своих размеров грид наружу не отдаёт и меток на строках
 * шапки не держит, а роль ячейки сохраняет намеренно (registry DataGrid). Отдай кит высоты строк
 * сам — измерение снимается.
 *
 * Опора внешняя и при смене разметки кита отвалится молча, поэтому она стоит в перечне опор на
 * разметку kit (`guideline.md`, раздел 3) — его читают при обновлении версии. Снимается измерение —
 * снимается и строка перечня.
 */
export const useGridHeaderRowHeights = (): Result => {
  const gridRef = useRef<HTMLDivElement>(null);

  const getHeaderRowHeights = useCallback((): number[] | undefined => {
    const headerCell = gridRef.current?.querySelector('[role="columnheader"]');
    const header = headerCell?.closest('[role="rowgroup"]');
    if (!header) {
      return undefined;
    }

    const headerBox = header.getBoundingClientRect();
    const rowTops = new Set<number>();
    for (const cell of header.querySelectorAll('[role="columnheader"]')) {
      rowTops.add(Math.round(cell.getBoundingClientRect().top - headerBox.top));
    }

    // Строка кончается там, где начинается следующая, а последняя — по нижнему краю шапки
    const sortedTops = [...rowTops].sort((first, second) => first - second);
    const heights = sortedTops.map((top, index) => (sortedTops[index + 1] ?? Math.round(headerBox.height)) - top);

    // Строка, высоту которой браузер не назвал, оставляет неизмеренной всю шапку: строки без неё
    // сместились бы в файле на чужие места, а к строке высота привязана только своим порядком
    return heights.length > 0 && heights.every((height) => Number.isFinite(height) && height > 0) ? heights : undefined;
  }, []);

  return { gridRef, getHeaderRowHeights };
};
